import { type MetaFunction } from '@remix-run/node'

export const meta: MetaFunction = () => [
	{ title: 'Spring Home Maintenance Tips' },
	{
		name: 'description',
		content: 'What to do to maintain your home in the spring.',
	},
]

export default function Component() {
	return (
		<>
			<h1 className="py-3 text-4xl font-black">
				Spring Home Maintenance Checklist
			</h1>
			<p
				data-paragraph
				className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
			>
				Home maintenance is an important practice to keep your home efficient,
				healthy, and structurally safe. Many of these home maintenance tips also
				preserve the value of your home and will save money on later repairs by
				keeping small problems from escalating into larger ones. When undergoing
				any home projects, please keep safety in mind at all times. Do not
				attempt any dangerous or unfamiliar tasks on your own. If you need help,
				consult a qualified technician or contractor.
			</p>
			<h2 className="py-3 pt-10 text-3xl font-black">Outside</h2>
			<section className="ml-3 mt-2">
				<h3 className="py-3 text-2xl font-black">Roof, Attic, and Gutters</h3>
				<p
					data-paragraph
					className="ml-3 mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10"
				>
					<ul>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							Inspect the roof from the ground. Look for any missing or damaged
							shingles as well as any bulges or sagging.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							Inspect the gutters and drainage system to verify that all parts
							are properly connected and that water is being routed at least
							four feet from the exterior walls of your home.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							Remove any debris from the gutters.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							Inspect the trim and fascia for damage.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							Inspect the roof from the inside of the attic to check for any
							signs of leakage or damage.
						</li>
					</ul>
				</p>
			</section>
			<section className="ml-3 mt-5">
				<h3 className="py-3 text-2xl font-black">Exterior</h3>
				<ul className="ml-3">
					<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
						Inspect wood surfaces including decks and steps. Check for rot,
						loose boards, or exposed nails.
					</li>
					<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
						Inspect all utility wires running to your home. Ensure that trees
						are not too close.
					</li>
					<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
						{' '}
						Consider removing dead trees.
					</li>
					<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
						Inspect walkways and driveway for damage. Fill any potholes and
						patch the cracks.
					</li>
				</ul>
			</section>
			<section className="ml-3 mt-5">
				<h3 className="py-3 text-2xl font-black">Windows &amp; Doors</h3>
				<p
					data-paragraph
					className="ml-3 mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10"
				>
					<ul>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							Inspect weather stripping around all windows and exterior doors.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Inspect screens for damage.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Inspect the handles and locks on all exterior doors. Ensure that
							they close tightly and that the locking mechanisms are secure.
						</li>
					</ul>
				</p>
			</section>
			<h2 className="py-3 pt-10 text-3xl font-black">Inside</h2>
			<section className="ml-3 mt-5">
				<h3 className="py-3 text-2xl font-black">Emergency Preparedness</h3>
				<p
					data-paragraph
					className="ml-3 mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10"
				>
					<ul>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Inspect your smoke detectors and fire extinguishers. The batteries
							in the smoke detectors need to be changed regularly and the gauge
							on the fire extinguisher will show the amount of pressure in the
							tank.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Hold a fire drill with family members. Practice with your children
							the escape routes from different parts of the house. Plan where
							you will meet if there is an alarm.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Consider a gas-powered generator to provide a backup power source
							to your home.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Consider storing a supply of nonperishable food and water. Rotate
							your emergency supply to prevent spoilage.
						</li>
					</ul>
				</p>
			</section>
			<section className="ml-3 mt-5">
				<h3 className="py-3 text-2xl font-black">Basement</h3>
				<p
					data-paragraph
					className="ml-3 mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10"
				>
					<ul>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Inspect foundation for any shifting or cracks. Seal any cracks.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Inspect wood for evidence of insect damage.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Check for any signs of moisture or mold. Address any problems
							immediately.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Consider a dehumidifier to keep air in the basement dry.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Consider a sump pump to prevent water from building up on the
							basement floor.
						</li>
					</ul>
				</p>
			</section>

			<section className="ml-3 mt-5">
				<h3 className="py-3 text-2xl font-black">Electrical</h3>
				<p
					data-paragraph
					className="ml-3 mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10"
				>
					<ul>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Test all Ground Fault Interrupter (GFI) Outlets.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Trip and reset all circuit breakers. (Be sure to power off any
							electronic devices prior to doing this).
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Inspect the main electrical panel for any signs of damage,
							especially burn marks.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Inspect outlets for poor wiring or bad connections. These can lead
							to electrical fires.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Consider replacing low-efficiency lightbulbs with high-efficiency
							bulbs.
						</li>
					</ul>
				</p>
			</section>
			<section className="ml-3 mt-5">
				<h3 className="py-3 text-2xl font-black">Plumbing</h3>
				<p
					data-paragraph
					className="ml-3 mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10"
				>
					<ul>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Use drain cleaning solution on any slow drains.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Remove buildup from plumbing traps.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Inspect water heater for any sign of water leakage. Make sure
							there is proper ventilation if you have a gas-powered heater.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Test shutoff valves for all plumbing fixtures.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Check all faucets and beneath every sink for evidence of leakage.
						</li>
					</ul>
				</p>
			</section>
			<section className="ml-3 mt-5">
				<h3 className="py-3 text-2xl font-black">Kitchen</h3>
				<p
					data-paragraph
					className="ml-3 mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10"
				>
					<ul>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Vacuum the refrigerator coils.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Remove any ice buildup from your freezer.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Deep-clean the oven and stove.
						</li>
					</ul>
				</p>
			</section>
			<section className="ml-3 mt-5">
				<h3 className="py-3 text-2xl font-black">Air Conditioning System</h3>
				<p
					data-paragraph
					className="ml-3 mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10"
				>
					<ul>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Clean the filter and condenser.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Listen for any abnormal sounds.
						</li>
						<li className="mt-2 text-xl/7 text-muted-foreground xl:mt-3 xl:text-xl/6 xl:leading-10">
							{' '}
							Check to make sure the system works before the heat sets in. If
							your system needs to be serviced, you should take care of it early
							on.
						</li>
					</ul>
				</p>
			</section>
			<hr className="mt-6" />
			<section className="ml-3 mt-5">
				<p
					data-paragraph
					className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
				>
					When the winter weather begins to fade away and the blossoms and green
					grass return, it is a good idea to do a full walk-around inspection of
					your home. Pay attention to the roof and take corrective action if you
					find anything to be out of the ordinary.
				</p>
				<p
					data-paragraph
					className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
				>
					By following these home maintenance tips, you can ensure that your
					home is in top condition and ready for the warmer months ahead.
				</p>
			</section>
		</>
	)
}
